import React from 'react'
import Reflux from 'reflux-react-16'

import aSync from 'async'
import _ from 'underscore'

import AddressStore from '../../stores/AddressStore.js'
import AddressActions from '../../actions/AddressActions.js'

import {Button, IconButton, Popup, P, Panel, Colors} from '../../components/UI/index.js'
import AddressModal from '../../components/addresses/AddressModal.js'
import ImportModal from '../../components/modals/importCSV/ImportModal.js'
import ChangeTimeslotPopup from '../../components/orders/ChangeTimeslotPopup.js'
import Table from '../../components/UI/table/index.js'
import SearchBar from '../../components/SearchBar/index.js'
import filter from '../../utils/filter.js'
import csvExport from '../../utils/csvExport.js'
import t from 'src/helpers/Languages.js'
import {getAvailableBranches} from '../../utils/branches.js'
import {changeTimeslot, normalizeTime, validateTimeslot} from '@utils/timeslot.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import {formSpacing} from '../../components/UI/forms/index.js'
import createMemo from '../../utils/createMemo.js'

class AddressBook extends Reflux.Component {
    constructor(props) {
        super(props)

        this.columnsMemo = createMemo()

        this.state = {
            selectedAddresses: [],
            queries: []
        }

        this.store = AddressStore
    }

    componentDidMount() {
        const {reseller} = this.props
        document.title = `Adresboek • ${reseller.settings.accountName || reseller.name}`
    }

    // The same columns until a value they read changes: new columns re-render every row of the table
    columns() {
        return this.columnsMemo([this.props.reseller], () => this.buildColumns())
    }

    buildColumns() {
        const {reseller} = this.props

        return [
            ...branchesAvailable(reseller) ?
                [{id: 'branchName', title: t('branch'), visible: false, key: 'branchName', width: 180, text: (address) => {
                    const branch = reseller.settings.branches?.find((branch) => branch.id === address?.branchId)
                    return branch?.name || ''
                }}] :
                [],
            {id: 'name', title: t('name'), visible: true, key: 'name', width: 300},
            {id: 'attention', title: t('attentionShort'), visible: true, key: 'attention', width: 100},
            {id: 'isBusiness', title: t('businessAddress'), visible: true, key: 'isBusiness', width: 80, text: (address) => {
                return address.isBusiness ? t('yes') : t('no')
            }},
            {id: 'address', title: t('address'), visible: true, key: 'address', flex: 2, text: ({street, nr, addition}) => `${street} ${nr}${addition}`},
            {id: 'postalCode', title: t('postalCode'), visible: true, key: 'postalCode', width: 100},
            {id: 'city', title: t('city'), visible: true, key: 'city', flex: 1},
            {id: 'country', title: t('country'), visible: true, key: 'country', width: 75},
            {id: 'email', title: t('email'), visible: true, key: 'email', flex: 1},
            {id: 'phone', title: t('phone'), visible: true, key: 'phone', width: 150},
            {id: 'instructions', title: t('instructions'), visible: true, key: 'instructions', flex: 1},
            {id: 'startTime', title: t('startTime'), visible: true, key: 'startTime', width: 90},
            {id: 'endTime', title: t('endTime'), visible: true, key: 'endTime', width: 90},
            {id: 'attachments', title: t('attachment'), visible: false, key: 'attachments', flex: 1, onClick: () => {}, render: (address) => {
                return (
                    <P
                        onClick={() => {
                            const attachment = address.attachments?.[0] || {}
                            const openUrl = (file) => {
                                const raw = window.atob(file)
                                const rawLength = raw.length
                                const array = new Uint8Array(new ArrayBuffer(rawLength))

                                for (let i = 0; i < rawLength; i++) {
                                    array[i] = raw.charCodeAt(i)
                                }

                                const blob = new Blob([array], {type: attachment.type})

                                const blobUrl = URL.createObjectURL(blob)

                                window.open(blobUrl)
                            }
                            if (attachment.fileId) {
                                AddressActions.downloadAttachment(attachment.fileId, (err, file) => {
                                    if (err) {
                                        console.log(err)
                                    } else {
                                        openUrl(file)
                                    }
                                })
                            } else if (attachment.data) {
                                window.open(attachment.data)
                            }
                        }}
                        ellipsis
                    >
                        {address.attachments?.map((a) => a.fileName).join(',')}
                    </P>
                )
            }},
            {id: 'edit', visible: true, showOnHover: true, width: 50, render: (address) => {
                return (
                    <IconButton onClick={this.onClickEdit.bind(this, address)}>
                        <i className='mdi mdi-pencil' />
                    </IconButton>
                )
            }}
        ]
    }

    onSaveAddress(address) {
        AddressActions.update([address], (err) => {
            if (err) {
                this.addressModal.setError(err)
            } else {
                this.addressModal.close()
            }
        })
    }

    onClickNew() {
        this.addressModal.open(undefined, this.onSaveAddress.bind(this))
    }

    onClickImport() {
        this.importModal.open()
    }

    onChangeImport(importedAddresses, columns, ignoreFirstLine) {
        const errors = importedAddresses.map((address, index) => {
            const error = validateTimeslot(address)
            return error ? t('importLineError', {line: index + (ignoreFirstLine ? 2 : 1), error: t(error)}) : ''
        }).filter(Boolean)

        if (errors.length) {
            this.importModal.setError(errors.map((error) => <div key={error}>{error}</div>))
            return
        }

        aSync.eachSeries(importedAddresses, (address, next) => {
            address = {
                name: address.name || '',
                attention: address.attention || '',
                street: address.street || '',
                nr: address.nr || '',
                addition: address.addition || '',
                street2: address.street2 || '',
                postalCode: address.postalCode || '',
                city: address.city || '',
                country: address.country || 'NL',
                email: address.email || '',
                phone: address.phone || '',
                instructions: address.instructions || '',
                // Columns that are not imported keep the timeslot already in the address book
                ...(columns.includes('startTime') && {startTime: normalizeTime(address.startTime)}),
                ...(columns.includes('endTime') && {endTime: normalizeTime(address.endTime)})
            }

            AddressActions.update([address], (err) => {
                if (err) {
                    this.importModal.setError(err)
                } else {
                    next()
                }
            })
        }).then(() => {
            this.importModal.close()
        })
    }

    async onClickExport() {
        const {selectedAddresses, addressBook} = this.state
        const exportAddresses = []

        selectedAddresses.map((id) => {
            const address = _.findWhere(addressBook, {_id: id})

            if (address) {
                exportAddresses.push(address)
            }
        })

        await this.popup.openProgress(t('export'), '', (onProgress) => csvExport.addresses(exportAddresses, onProgress, true))

        this.setState({selectedAddresses: []})
    }

    onClickEdit(address) {
        this.addressModal.open(address, this.onSaveAddress.bind(this))
    }

    async onClickAssignBranch() {
        const {selectedAddresses, addressBook} = this.state
        const {reseller} = this.props

        const availableBranches = getAvailableBranches(reseller)

        this.popup.setInputs([
            {label: t('branch'), name: 'branchId', value: '', type: 'select', options: [{value: '', title: t('noBranch')}, ...availableBranches.map((branch) => ({value: branch.id, title: branch.name}))]}
        ])

        const values = await this.popup.open(t('assignBranch'), t('chooseBranchForAddresses'))
        if (!values) return

        const branchId = values.branchId || ''

        let success = 0
        const errors = []
        this.popup.setProgress(0, selectedAddresses.length)

        for (const id of selectedAddresses) {
            const address = _.findWhere(addressBook, {_id: id})

            if (!address) {
                continue
            }

            await new Promise((resolve) => {
                AddressActions.update([{...address, branchId}], (err) => {
                    if (err) {
                        errors.push(err)
                    } else {
                        success += 1
                    }

                    this.popup.setProgress(success + errors.length, selectedAddresses.length)
                    resolve()
                })
            })
        }

        this.setState({selectedAddresses: []})
        this.popup.setMessage(t('addressesEdited', {count: success}))
        this.popup.setErrors(errors)
    }

    onClickChangeTimeslot() {
        this.changeTimeslotPopup.open(this.state.selectedAddresses)
    }

    async onConfirmChangeTimeslot(change, ids) {
        const {addressBook} = this.state

        let success = 0
        const errors = []

        this.popup.open(t('changeTimeslot'))
        this.popup.setLoading(true)
        this.popup.setProgress(0, ids.length)

        for (const id of ids) {
            const address = _.findWhere(addressBook, {_id: id})

            if (!address) {
                continue
            }

            const timeslot = changeTimeslot(address, change)

            // Checked here so the address can be named, the server's error doesn't name it
            const error = validateTimeslot(timeslot)

            if (error) {
                const name = address.name || `${address.street} ${address.nr}${address.addition}`
                errors.push(error === 'endTimeBeforeStartTime' ? t('addressEndTimeNotAfterStartTime', {name}) : `${name}: ${t(error)}`)
                this.popup.setProgress(success + errors.length, ids.length)
                continue
            }

            await new Promise((resolve) => {
                AddressActions.update([{...address, ...timeslot}], (err) => {
                    if (err) {
                        errors.push(err)
                    } else {
                        success += 1
                    }

                    this.popup.setProgress(success + errors.length, ids.length)
                    resolve()
                })
            })
        }

        this.setState({selectedAddresses: []})
        this.popup.setMessage(t('addressesEdited', {count: success}))
        this.popup.setErrors(errors)
    }

    onClickRemove() {
        const {selectedAddresses} = this.state

        this.popup.open('Adressen verwijderen', 'Adressen worden definitief verwijderd.', () => {
            AddressActions.remove(selectedAddresses)
            this.popup.close()
            this.setState({selectedAddresses: []})
        })
    }

    onChangeQueries(queries) {
        this.table.selectPage(1)
        this.setState({queries})
    }

    onTableChange(selectedAddresses) {
        this.setState({selectedAddresses})
    }

    render() {
        const {queries, selectedAddresses, addressBook} = this.state
        const {reseller} = this.props

        const addresses = filter.addresses(addressBook, queries, reseller)
        const availableBranches = getAvailableBranches(reseller)

        return (
            <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Panel style={{display: 'flex', columnGap: formSpacing.columnGap, borderTop: 'none', borderLeft: 'none', borderRight: 'none', background: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>

                    <SearchBar
                        columns={this.columns()}
                        onQueryChange={this.onChangeQueries.bind(this)}
                        queries={queries}
                    />

                    <Button
                        variant='outline-white'
                        onClick={this.onClickNew.bind(this)}
                    >
                        Nieuw adres
                    </Button>

                    <Button
                        variant='outline-white'
                        onClick={this.onClickImport.bind(this)}
                    >
                        <i className='mdi mdi-file-import' />
                    </Button>

                    {selectedAddresses.length > 0 &&
                        <>
                            <Button
                                variant='outline-white'
                                onClick={this.onClickExport.bind(this)}
                            >
                                <i className='mdi mdi-file-export' />
                            </Button>

                            {availableBranches.length > 0 &&
                                <Button
                                    variant='outline-white'
                                    onClick={this.onClickAssignBranch.bind(this)}
                                    tooltip={t('assignBranch')}
                                >
                                    <i className='mdi mdi-office-building-marker-outline' />
                                </Button>
                            }

                            <Button
                                variant='outline-white'
                                onClick={this.onClickChangeTimeslot.bind(this)}
                                tooltip={t('changeTimeslot')}
                            >
                                <i className='mdi mdi-clock-edit-outline' />
                            </Button>

                            <Button
                                variant='outline-white'
                                onClick={this.onClickRemove.bind(this)}
                            >
                                <i className='mdi mdi-delete' />
                            </Button>
                        </>
                    }
                </Panel>

                <div style={{flex: 1, marginTop: 24, marginRight: 24, marginLeft: 24}}>
                    <Table
                        tableName='addressBook'
                        columns={this.columns()}
                        rows={addresses}
                        selectedRows={selectedAddresses}
                        onChange={this.onTableChange.bind(this)}
                        ref={(ref) => this.table = ref}
                    />
                </div>

                <AddressModal
                    reseller={this.props.reseller}
                    showInstructions
                    showTimeslot
                    allowEmptyName
                    ref={(ref) => this.addressModal = ref}
                />

                <ImportModal
                    ref={(ref) => this.importModal = ref}
                    possibleColumns={possibleColumns}
                    isAddressBook
                    onChange={this.onChangeImport.bind(this)}
                />

                <ChangeTimeslotPopup
                    text={t('changeTimeslotAddressesText')}
                    countText={(count) => t('addressesSelectedCount', {count})}
                    onConfirm={this.onConfirmChangeTimeslot.bind(this)}
                    requireField
                    ref={(ref) => this.changeTimeslotPopup = ref}
                />

                <Popup ref={(ref) => this.popup = ref} />
            </div>
        )
    }
}

const possibleColumns = {
    name: t('name'),
    attention: t('attentionShort'),
    street: t('street'),
    nr: t('nrShort'),
    addition: t('addition'),
    streetNr: t('streetNrAddition'),
    nrAddition: t('nrAddition'),
    street2: t('street2'),
    postalCode: t('postalCode'),
    city: t('city'),
    country: t('country'),
    email: t('emailAddress'),
    phone: t('phone'),
    instructions: t('instructions'),
    startTime: t('startTimeFormat'),
    endTime: t('endTimeFormat')
}

export default AddressBook
